import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Users, 
  Heart, 
  Star, 
  Award, 
  Calendar, 
  BookOpen, 
  Music, 
  ArrowRight,
  Check,
  Crown,
  Shield,
  Sparkles,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import { membershipService } from "@/services/membershipService";
import type { MembershipType, MembershipLength } from "@/types/membership";
import { Page } from "@/types/page";
import { withContentManagement } from "@/components/pages/withContentManagement";
import { RenderContent } from "@/components/pages/RenderContent";

interface MembershipProps {
  page?: Page;
}

const Membership = ({ page }: MembershipProps) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    organization: "",
    membershipType: "" as MembershipType | "",
    membershipLength: "" as MembershipLength | "",
    agreeToTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const membershipTypes = [
    {
      type: "Student",
      icon: BookOpen,
      price: "$15",
      description: "Perfect for students and young enthusiasts",
      benefits: [
        "Access to all events",
        "Student workshop discounts",
        "Library privileges",
        "Monthly newsletter"
      ],
      color: "border-accent/30 hover:border-accent"
    },
    {
      type: "Adult",
      icon: Users,
      price: "$35",
      description: "Full membership for community members",
      benefits: [
        "All student benefits",
        "Priority event registration",
        "Member appreciation events",
        "Volunteer opportunities",
        "Workshop access"
      ],
      color: "border-heritage/30 hover:border-heritage",
      popular: true
    },
    {
      type: "Veteran",
      icon: Shield,
      price: "$25",
      description: "Special recognition for our military veterans",
      benefits: [
        "All adult member benefits",
        "Veterans Day celebration access",
        "Military heritage programs",
        "Special veteran community events",
        "Military history research access"
      ],
      color: "border-culture/30 hover:border-culture"
    },
    {
      type: "Organization",
      icon: Award,
      price: "$100",
      description: "For community organizations and nonprofits",
      benefits: [
        "Corporate recognition",
        "Bulk event tickets",
        "Partnership opportunities",
        "Newsletter feature opportunities",
        "Meeting space rental discounts"
      ],
      color: "border-primary/30 hover:border-primary"
    },
    {
      type: "Business",
      icon: Crown,
      price: "$200",
      description: "Business partnership and community investment",
      benefits: [
        "All organization benefits",
        "Logo placement on website",
        "Event sponsorship opportunities",
        "Business networking events",
        "Community leadership recognition",
        "Annual business appreciation dinner"
      ],
      color: "border-accent/50 hover:border-accent",
      premium: true
    }
  ];

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!formData.membershipType || !formData.membershipLength) {
        toast.error("Please select a membership type and length");
        setIsSubmitting(false);
        return;
      }

      const result = await membershipService.submitMembershipApplication({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        organization: formData.organization?.trim() || undefined,
        membershipType: formData.membershipType as MembershipType,
        membershipLength: formData.membershipLength as MembershipLength,
        agreeToTerms: formData.agreeToTerms,
      });

      if (result.success) {
        setIsSubmitted(true);
        toast.success("Thank you for your membership application! We'll review it and get back to you soon.");
        
        // Reset form after delay
        setTimeout(() => {
          setFormData({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            organization: "",
            membershipType: "" as MembershipType | "",
            membershipLength: "" as MembershipLength | "",
            agreeToTerms: false
          });
          setIsSubmitted(false);
        }, 5000);
      } else {
        toast.error(result.error.getUserMessage() || "Failed to submit application. Please try again.");
      }
    } catch (error) {
      console.error('Error submitting membership application:', error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedMembership = membershipTypes.find(m => m.type === formData.membershipType);

  return (
    <div className="min-h-screen bg-gradient-warm">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Join Our <span className="text-heritage">Community</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Become part of a vibrant community dedicated to preserving our heritage,
            celebrating diversity, and fostering meaningful connections.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="flex items-center text-heritage">
              <Users className="w-5 h-5 mr-2" />
              <span className="font-medium">500+ Active Members</span>
            </div>
            <div className="flex items-center text-heritage">
              <Calendar className="w-5 h-5 mr-2" />
              <span className="font-medium">50+ Annual Events</span>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Form */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-heritage border-heritage/20">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Join Our Community</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <form className="space-y-6">
                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter your email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="organization">Organization</Label>
                  <Input
                    id="organization"
                    value={formData.organization}
                    onChange={(e) => handleInputChange('organization', e.target.value)}
                    placeholder="If applicable"
                  />
                </div>

                {/* Membership Selection */}
                <div>
                  <Label>Membership Type *</Label>
                  <Select onValueChange={(value) => handleInputChange('membershipType', value)} value={formData.membershipType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select membership type" />
                    </SelectTrigger>
                    <SelectContent>
                      {membershipTypes.map((type) => (
                        <SelectItem key={type.type} value={type.type}>
                          {type.type} - {type.price}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Membership Length */}
                <div>
                  <Label>Membership Length *</Label>
                  <RadioGroup
                    value={formData.membershipLength}
                    onValueChange={(value) => handleInputChange('membershipLength', value)}
                    className="flex flex-col sm:flex-row gap-6 mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yearly-onetime" id="yearly-onetime" />
                      <Label htmlFor="yearly-onetime">Yearly (One-time payment)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yearly-monthly" id="yearly-monthly" />
                      <Label htmlFor="yearly-monthly">Yearly (Monthly recurring)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="monthly" id="monthly" />
                      <Label htmlFor="monthly">Monthly</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Terms Agreement */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="terms"
                    checked={formData.agreeToTerms}
                    onCheckedChange={(checked) => handleInputChange('agreeToTerms', checked as boolean)}
                  />
                  <Label htmlFor="terms" className="text-sm">
                    I agree to the membership terms and conditions and understand that my membership supports our community. *
                  </Label>
                </div>

                {/* Submit Button */}
                <div className="flex flex-col sm:flex-row gap-4 pt-6">
                  {isSubmitted ? (
                    <div className="flex-1 text-center py-4">
                      <div className="flex items-center justify-center text-heritage mb-2">
                        <CheckCircle className="w-6 h-6 mr-2" />
                        <span className="text-lg font-semibold">Application Submitted Successfully!</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Thank you for your interest in becoming a member. We'll review your application and get back to you soon.
                      </p>
                    </div>
                  ) : (
                    <>
                      <Button
                        type="submit"
                        variant="heritage"
                        size="lg"
                        className="flex-1"
                        disabled={isSubmitting || !formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.membershipType || !formData.membershipLength || !formData.agreeToTerms}
                      >
                        <Heart className="w-5 h-5 mr-2" />
                        {isSubmitting ? "Submitting..." : "Join Now"}
                        {!isSubmitting && <ArrowRight className="w-4 h-4 ml-2" />}
                      </Button>
                      <Button type="button" variant="outline" size="lg" disabled>
                        Save as Draft
                      </Button>
                    </>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Member Benefits */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-foreground mb-12">
            Why Join Our Community?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <Music className="w-12 h-12 text-heritage mx-auto mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Celebrations</h3>
              <p className="text-muted-foreground">
                Access to exclusive events, performances, and celebrations throughout the year.
              </p>
            </div>
            <div className="text-center">
              <BookOpen className="w-12 h-12 text-heritage mx-auto mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Educational Programs</h3>
              <p className="text-muted-foreground">
                Participate in workshops, classes, and educational initiatives that preserve and share knowledge.
              </p>
            </div>
            <div className="text-center">
              <Users className="w-12 h-12 text-heritage mx-auto mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Community Connection</h3>
              <p className="text-muted-foreground">
                Connect with like-minded individuals who share your passion for heritage and community building.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Membership;